/**
 * The flat top bar: the wordmark, three sections, the account.
 *
 * The Studio's cluster — "N making", the generations budget — is deliberately absent. Those
 * are a *parent's* facts about a *parent's* work; the admin has neither. What the cluster
 * carries instead is the **database name**, because the one mistake this app could make
 * quietly is editing the wrong one.
 *
 * The account menu says how the account comes in (link, password, Google — informational),
 * offers a password to set or change, and carries the quiet verify-your-email line for a
 * password account that has not opened its link yet. Nothing is gated on that line.
 */

import {
  Avatar,
  MenuAction,
  MenuButton,
  MenuGroup,
  MenuHeading,
  MenuNote,
} from '@atlas-nook/design-system';
import { Wordmark } from '@atlas-nook/design-system';
import { useState } from 'react';
import type { JSX } from 'react';
import { NavLink } from 'react-router';

import { useAuth, useSession } from '../auth/AuthProvider.js';
import type { SignInMethod } from '../auth/AuthProvider.js';
import { PasswordDialogs } from '../auth/PasswordDialogs.js';
import type { PasswordDialogKind } from '../auth/PasswordDialogs.js';
import { DATABASE_NAME } from '../data/firestore.js';
import { ADMIN_STRINGS as S } from '../strings.js';
import { useToast } from './ToastProvider.js';

const METHOD_LABEL: Record<SignInMethod, string> = {
  link: S.account.methodLink,
  password: S.account.methodPassword,
  google: S.account.methodGoogle,
  email: S.account.methodEmail,
};

export function TopBar(): JSX.Element {
  const session = useSession();
  const { signOutNow, sendVerification, verifyNoteDismissed, dismissVerifyNote } = useAuth();
  const toast = useToast();
  const [dialog, setDialog] = useState<PasswordDialogKind>(null);

  const methods = session.methods.map((method) => METHOD_LABEL[method]);
  const hasPassword = session.methods.includes('password');
  const showVerifyNote = !session.emailVerified && !verifyNoteDismissed;

  return (
    <header className="app-top">
      <Wordmark compact name={S.app.name} descriptor={S.app.descriptor} />

      <nav className="navrow" aria-label={S.nav.sections}>
        <NavLink className="navitem" to="/mediums">
          {S.nav.catalog}
        </NavLink>
        <NavLink className="navitem" to="/globals">
          {S.nav.globals}
        </NavLink>
        <NavLink className="navitem" to="/issues">
          {S.nav.issues}
        </NavLink>
      </nav>

      <div className="top-end">
        <div className="cluster">
          <span className="cost">{S.nav.database(DATABASE_NAME)}</span>
          <span className="divider" aria-hidden="true" />
          <MenuButton
            label={S.nav.account(session.email)}
            trigger={
              <Avatar trigger initials={session.initials} label={S.nav.account(session.email)} />
            }
          >
            <MenuGroup>
              <MenuHeading>{session.email}</MenuHeading>
              {methods.length > 0 && (
                <MenuHeading>{S.account.methods(methods.join(' · '))}</MenuHeading>
              )}
              <MenuAction
                id={hasPassword ? 'change-password' : 'set-password'}
                onAction={() => setDialog(hasPassword ? 'change' : 'set')}
              >
                {hasPassword ? S.account.changePassword : S.account.setPassword}
              </MenuAction>
              <MenuAction id="signout" onAction={() => void signOutNow()}>
                {S.nav.signOut}
              </MenuAction>
            </MenuGroup>

            {/* The same note the Studio's menu carries, in the same block — one shape for
                one message, in both apps (`studio/13`). */}
            {showVerifyNote && (
              <MenuNote
                text={
                  <>
                    <p>{S.account.verifyNote}</p>
                    <p>{S.account.verifyConsequence}</p>
                  </>
                }
              >
                <MenuAction
                  id="verify-resend"
                  onAction={() => {
                    sendVerification()
                      .then(() => toast.show(S.account.verifySent))
                      .catch(() => toast.show(S.signin.failed));
                  }}
                >
                  {S.account.verifyResend}
                </MenuAction>
                <MenuAction id="verify-dismiss" onAction={dismissVerifyNote}>
                  {S.account.verifyDismiss}
                </MenuAction>
              </MenuNote>
            )}
          </MenuButton>
        </div>
      </div>

      <PasswordDialogs open={dialog} onClose={() => setDialog(null)} />
    </header>
  );
}
